import axiosInstance from '../../../core/api/interceptors/axiosInterceptor';

/**
 * Create a new sales receipt/ticket
 * @param {Object} ticketData - Ticket data matching CreateTicketDto
 * @returns {Promise<Object>} Created ticket
 */
export const createTicket = async (ticketData) => {
  const response = await axiosInstance.post('Ticket/create-sale-ticket', ticketData);
  return response.data;
};

/**
 * Get all tickets
 * @returns {Promise<Array>} List of all tickets
 */
export const getAllTickets = async () => {
  const response = await axiosInstance.get('Ticket');
  return response.data;
};

/**
 * Get ticket by ID
 * @param {number} id - Ticket ID
 * @returns {Promise<Object>} Ticket details
 */
export const getTicketById = async (id) => {
  const response = await axiosInstance.get(`Ticket/${id}`);
  return response.data;
};

/**
 * Get ticket by ticket number
 * @param {string} ticketNumber - Ticket number (e.g., TKT-12345678-123)
 * @returns {Promise<Object>} Ticket details
 */
export const getTicketByNumber = async (ticketNumber) => {
  const response = await axiosInstance.get(`Ticket/by-number/${ticketNumber}`);
  return response.data;
};

/**
 * Get tickets by customer ID
 * @param {number} customerId - Customer ID
 * @returns {Promise<Array>} List of customer tickets
 */
export const getTicketsByCustomer = async (customerId) => {
  const response = await axiosInstance.get(`Ticket/by-customer/${customerId}`);
  return response.data;
};

/**
 * Get tickets by date range
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @returns {Promise<Array>} List of tickets in date range
 */
export const getTicketsByDateRange = async (startDate, endDate) => {
  const response = await axiosInstance.get('Ticket/by-date-range', {
    params: { startDate, endDate }
  });
  return response.data;
};

/**
 * Get my sales (current user's sales)
 * @returns {Promise<Array>} List of current user's tickets
 */
export const getMySales = async () => {
  const response = await axiosInstance.get('Ticket/my-sales');
  return response.data;
};

/**
 * Get today's sales
 * @returns {Promise<Array>} List of today's tickets
 */
export const getTodaysSales = async () => {
  const response = await axiosInstance.get('Ticket/today');
  return response.data;
};

/**
 * Update ticket (refund, add notes, etc.)
 * @param {number} id - Ticket ID
 * @param {Object} updateData - Update data matching UpdateTicketDto
 * @returns {Promise<Object>} Updated ticket
 */
export const updateTicket = async (id, updateData) => {
  const response = await axiosInstance.put(`Ticket/${id}`, {
    ...updateData,
    Id: id
  });
  return response.data;
};

/**
 * Delete ticket
 * @param {number} id - Ticket ID
 * @returns {Promise<void>}
 */
export const deleteTicket = async (id) => {
  await axiosInstance.delete(`Ticket/${id}`);
};

/**
 * Get sales statistics
 * @param {Date|string} startDate - Optional start date
 * @param {Date|string} endDate - Optional end date
 * @returns {Promise<Object>} Sales statistics
 */
export const getStatistics = async (startDate = null, endDate = null) => {
  const params = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  
  const response = await axiosInstance.get('Ticket/statistics', { params });
  return response.data;
};

/**
 * Transform cart data to CreateTicketDto format
 * @param {Object} params - Parameters for ticket creation
 * @returns {Object} Formatted ticket data
 */
export const transformCartToTicketDto = ({
  cart,
  customer,
  selectedPaymentMethod,
  paidAmount,
  discount,
  customVATRate,
  receiptNumber,
  notes = ''
}) => {
  // Calculate subtotal (sum of all items before VAT and discount)
  const subtotal = cart.reduce((sum, item) => sum + item.Quantity * item.Price, 0);

  // Determine VAT percentage (use custom VAT rate or default from items)
  const vatPercentage = customVATRate !== null && customVATRate !== undefined 
    ? customVATRate 
    : 21;

  // Calculate discount details
  let discountType = null;
  let discountPercentage = null;
  let discountReason = null;
  let discountAmount = 0;

  if (discount) {
    if (discount.type === 'percentage') {
      discountType = 'Percentage';
      discountPercentage = discount.value;
      discountAmount = (subtotal * discount.value) / 100;
    } else {
      discountType = 'Fixed';
      discountAmount = discount.value;
    }
    discountReason = discount.reason || null;
  }

  // Calculate totals
  const subtotalAfterDiscount = subtotal - discountAmount;
  const vatAmount = (subtotalAfterDiscount * vatPercentage) / 100;
  const totalAmountInclVAT = subtotal + (subtotal * vatPercentage) / 100; // Before discount
  const totalAmountInclVATAfterDiscount = subtotalAfterDiscount + vatAmount;

  // Calculate change
  const paid = parseFloat(paidAmount) || 0;
  const amountInChange = Math.max(0, paid - totalAmountInclVATAfterDiscount);

  // Transform line items
  const lineItems = cart.map(item => {
    const itemVAT = vatPercentage;
    const quantity = parseFloat(item.Quantity) || 1;
    const unitPriceExclTax = parseFloat(item.Price) || 0;
    const unitPriceInclVat = unitPriceExclTax * (1 + itemVAT / 100);

    // Calculate item discount
    let itemDiscountPercentage = 0;
    let itemDiscountAmount = 0;
    if (discount && discount.type === 'percentage') {
      itemDiscountPercentage = discount.value;
    }

    // Calculate line item totals
    const lineSubtotal = quantity * unitPriceExclTax;
    const lineDiscountAmount = (lineSubtotal * itemDiscountPercentage) / 100;
    const lineSubtotalAfterDiscount = lineSubtotal - lineDiscountAmount;
    const lineVATAmount = (lineSubtotalAfterDiscount * itemVAT) / 100;
    const lineTotalInclVAT = lineSubtotal + (lineSubtotal * itemVAT / 100); // Before discount
    const lineTotalInclVATAfterDiscount = lineSubtotalAfterDiscount + lineVATAmount;

    return {
      ProductId: item.Id,
      Quantity: quantity,
      UnitPriceExclTax: Math.round(unitPriceExclTax * 100) / 100,
      UnitPriceInclVat: Math.round(unitPriceInclVat * 100) / 100,
      Discount: itemDiscountPercentage,
      DiscountAmount: Math.round(lineDiscountAmount * 100) / 100,
      VATAmount: Math.round(lineVATAmount * 100) / 100,
      Subtotal: Math.round(lineSubtotal * 100) / 100,
      TotalAmountInclVATAfterDiscount: Math.round(lineTotalInclVATAfterDiscount * 100) / 100,
      TotalAmountInclVAT: Math.round(lineTotalInclVAT * 100) / 100
    };
  });

  const ticketDto = {
    CustomerId: customer?.Id || null,
    CustomerName: customer?.Name || 'Walk-in Customer',
    PaymentMethod: selectedPaymentMethod || 'Cash',
    Subtotal: Math.round(subtotal * 100) / 100,
    VATPercentage: vatPercentage,
    VATAmount: Math.round(vatAmount * 100) / 100,
    DiscountType: discountType,
    DiscountPercentage: discountPercentage,
    DiscountAmount: Math.round(discountAmount * 100) / 100,
    DiscountReason: discountReason,
    TotalAmountInclVATAfterDiscount: Math.round(totalAmountInclVATAfterDiscount * 100) / 100,
    TotalAmountInclVAT: Math.round(totalAmountInclVAT * 100) / 100,
    PaidAmount: paid > 0 ? Math.round(paid * 100) / 100 : null,
    AmountInChange: Math.round(amountInChange * 100) / 100,
    Notes: notes || (discountReason ? `Discount: ${discountReason}` : ''),
    LineItems: lineItems
  };

  console.log('📋 Transformed Ticket DTO:', ticketDto);
  return ticketDto;
};

/**
 * Create ticket from cart and print
 * @param {Object} params - All required parameters for ticket creation
 * @returns {Promise<Object>} Created ticket with print status
 */
export const createAndPrintTicket = async (params) => {
  try {
    // Transform cart to ticket DTO
    const ticketDto = transformCartToTicketDto(params);
    
    // Create ticket in database
    const createdTicket = await createTicket(ticketDto);
    
    return {
      success: true,
      ticket: createdTicket,
      message: 'Ticket created successfully'
    };
  } catch (error) {
    console.error('Error creating ticket:', error);
    return {
      success: false,
      error: error.message || 'Failed to create ticket',
      message: 'Failed to create ticket in database'
    };
  }
};

/**
 * Validate ticket data before submission
 * @param {Object} ticketData - Ticket data to validate
 * @returns {Object} Validation result
 */
export const validateTicketData = (ticketData) => {
  const errors = [];

  if (!ticketData.PaymentMethod) {
    errors.push('Payment method is required');
  }

  if (!ticketData.PaidAmount || ticketData.PaidAmount <= 0) {
    errors.push('Paid amount must be greater than 0');
  }

  if (!ticketData.LineItems || ticketData.LineItems.length === 0) {
    errors.push('At least one product is required');
  }

  if (ticketData.LineItems) {
    ticketData.LineItems.forEach((item, index) => {
      if (!item.ProductId) {
        errors.push(`Line item ${index + 1}: Product ID is required`);
      }
      if (!item.Quantity || item.Quantity <= 0) {
        errors.push(`Line item ${index + 1}: Quantity must be greater than 0`);
      }
      // if (item.UnitPrice === undefined || item.UnitPrice < 0) {
      //   errors.push(`Line item ${index + 1}: Unit price must be positive`);
      // }
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Export all functions
export default {
  createTicket,
  getAllTickets,
  getTicketById,
  getTicketByNumber,
  getTicketsByCustomer,
  getTicketsByDateRange,
  getMySales,
  getTodaysSales,
  updateTicket,
  deleteTicket,
  getStatistics,
  transformCartToTicketDto,
  createAndPrintTicket,
  validateTicketData
};